import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n/LanguageContext";
import { useGoTo } from "@/lib/navigation";

export default function Hero() {
  const { t } = useLang();
  const h = t.hero;
  const scrollTo = useGoTo();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 sm:pt-40 pb-24 sm:pb-32 overflow-hidden bg-cream-50"
    >
      <div className="absolute inset-0 -z-10 grid-lines opacity-70" />
      <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-brand-100/50 blur-3xl -z-10" />

      <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7 animate-fade-up">
          <div className="flex items-center gap-3 mb-6">
            <span className="hairline" />
            <span className="text-overline">{h.overline}</span>
          </div>
          <h1
            data-testid="hero-headline"
            className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink-900 font-extrabold"
          >
            {h.h1a}{" "}
            <span className="text-brand-500 accent-underline">{h.h1accent}</span> {h.h1b}
          </h1>
          <p
            data-testid="hero-subheadline"
            className="mt-7 text-base sm:text-lg text-ink-500 max-w-2xl leading-relaxed"
          >
            {h.sub}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Button
              data-testid="hero-primary-cta"
              onClick={() => scrollTo("#book")}
              className="group bg-brand-500 text-white hover:bg-brand-600 hover:-translate-y-0.5 transition-all duration-300 rounded-sm h-12 px-7 text-sm tracking-wide font-semibold shadow-sm"
            >
              {h.cta}
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button
              data-testid="hero-secondary-cta"
              onClick={() => scrollTo("/services")}
              variant="outline"
              className="bg-white border-ink-900/12 text-ink-900 hover:bg-cream-100 hover:text-brand-600 hover:border-brand-500 rounded-sm h-12 px-7 text-sm tracking-wide font-semibold"
            >
              {h.cta2}
            </Button>
          </div>

          <ul className="mt-10 flex flex-wrap gap-x-7 gap-y-3">
            {h.trust.map((tp, i) => (
              <li
                key={i}
                data-testid={`hero-trust-${i + 1}`}
                className="flex items-center gap-2 text-sm text-ink-600"
              >
                <CheckCircle2 className="w-4 h-4 text-brand-500" />
                {tp}
              </li>
            ))}
          </ul>
        </div>

        <div className="hidden lg:block lg:col-span-5 animate-fade-up" style={{ animationDelay: "150ms" }}>
          <div className="relative">
            <div className="absolute -bottom-4 -right-4 w-full h-full bg-brand-500 -z-10" />
            <div className="relative bg-white border border-ink-900/8 p-8 shadow-lg">
              <p className="text-overline mb-5">{h.glance}</p>
              <div className="space-y-5">
                {h.stats.map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between border-b border-ink-900/6 pb-3">
                    <span className="text-sm text-ink-500">{k}</span>
                    <span className="font-display font-bold text-lg text-ink-900">{v}</span>
                  </div>
                ))}
              </div>
              <div className="mt-7 pt-5 border-t border-ink-900/6">
                <p className="text-xs text-ink-500 leading-relaxed">{h.glanceNote}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
